/**
 * Date maths for the date picker. Dates are plain YYYY-MM-DD strings (no time zone);
 * weeks start on Monday (RULES.md rule 9).
 */

export interface DayCell {
  iso: string;
  day: number;
  /** False for the grey days of the previous/next month that fill the grid. */
  inMonth: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');

export function toIso(year: number, month: number, day: number): string {
  const dt = new Date(Date.UTC(year, month, day));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

export function parseIso(iso: string): { year: number; month: number; day: number } {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return { year: y, month: m - 1, day: d };
}

/** Six Monday-first weeks covering `month` (0-based), padded with neighbouring days. */
export function monthGrid(year: number, month: number): DayCell[] {
  const first = new Date(Date.UTC(year, month, 1));
  const lead = (first.getUTCDay() + 6) % 7; // Monday = 0
  return Array.from({ length: 42 }, (_, i) => {
    const dt = new Date(Date.UTC(year, month, 1 - lead + i));
    return {
      iso: `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`,
      day: dt.getUTCDate(),
      inMonth: dt.getUTCMonth() === month,
    };
  });
}

/** Moves an ISO date by days (keyboard arrows) or months (Page Up/Down), clamping the day. */
export function shiftDays(iso: string, days: number): string {
  const { year, month, day } = parseIso(iso);
  return toIso(year, month, day + days);
}

export function shiftMonths(iso: string, months: number): string {
  const { year, month, day } = parseIso(iso);
  const lastDay = new Date(Date.UTC(year, month + months + 1, 0)).getUTCDate();
  return toIso(year, month + months, Math.min(day, lastDay));
}

export function inRange(iso: string, min?: string, max?: string): boolean {
  return (!min || iso >= min) && (!max || iso <= max);
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export const monthName = (month: number) => MONTHS[month] ?? '';

/** "7 Oct 2026". */
export function formatDay(iso: string): string {
  const { year, month, day } = parseIso(iso);
  return `${day} ${monthName(month).slice(0, 3)} ${year}`;
}

/** "2026-09" → "September 2026". */
export function formatMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return `${monthName(m - 1)} ${y}`;
}
